import { useEffect, useMemo, useRef, useState } from 'react';
import { formatoNumero, formatoPorcentaje } from '@/lib/utils/formato-precio';
import { useNavigate } from 'react-router-dom';
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from 'react-simple-maps';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { MapPin, Package, Weight, Ruler, Calendar, X, Globe, Ship, BoxSelect } from 'lucide-react';
import { useImportTrackingStore } from '../store/import-tracking-store';
import { ContainerStatusBadge } from './container-status-badge';
import type { PortSummary, AvailableContainer } from '../types/import-tracking.types';

// ── Constants ────────────────────────────────────────────────────

const GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/land-110m.json';

type SortField = 'fillPercent' | 'availableVolumeCbm' | 'etd';
type SortOrder = 'asc' | 'desc';

const SORT_OPTIONS = [
  { value: 'fillPercent-asc', label: '% Llenado (menor primero)' },
  { value: 'fillPercent-desc', label: '% Llenado (mayor primero)' },
  { value: 'availableVolumeCbm-desc', label: 'Volumen disponible (mayor primero)' },
  { value: 'availableVolumeCbm-asc', label: 'Volumen disponible (menor primero)' },
  { value: 'etd-asc', label: 'ETD (más pronto primero)' },
  { value: 'etd-desc', label: 'ETD (más lejano primero)' },
];

// ── Helpers ──────────────────────────────────────────────────────

function getFillColor(fillPercent: number): string {
  if (fillPercent < 40) return '#22c55e'; // green-500
  if (fillPercent < 70) return '#eab308'; // yellow-500
  if (fillPercent < 90) return '#f97316'; // orange-500
  return '#ef4444'; // red-500
}

function getFillColorClass(fillPercent: number): string {
  if (fillPercent < 40) return 'bg-ok';
  if (fillPercent < 70) return 'bg-alerta';
  if (fillPercent < 90) return 'bg-alerta';
  return 'bg-bloqueo';
}

function getFillTextClass(fillPercent: number): string {
  if (fillPercent < 40) return 'text-ok';
  if (fillPercent < 70) return 'text-alerta';
  if (fillPercent < 90) return 'text-alerta';
  return 'text-bloqueo';
}

function getMarkerSize(containerCount: number): number {
  const minSize = 8;
  const maxSize = 22;
  const clampedCount = Math.min(Math.max(containerCount, 1), 20);
  return minSize + ((clampedCount - 1) / 19) * (maxSize - minSize);
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleDateString('es-BO', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

// ── Port Marker Component ─────────────────────────────────────

interface PortMarkerProps {
  port: PortSummary & { lat: number; lng: number };
  isSelected: boolean;
  onSelect: (port: string) => void;
}

function PortMarker({ port, isSelected, onSelect }: PortMarkerProps) {
  const [hovered, setHovered] = useState(false);
  const size = getMarkerSize(port.totalContainers);
  const color = getFillColor(port.avgFillPercent);

  return (
    <Marker coordinates={[port.lng, port.lat]}>
      <g
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={() => onSelect(port.port)}
        className="cursor-pointer"
      >
        {/* Pulse animation for selected */}
        {isSelected && <circle r={size + 6} fill={color} opacity={0.2} className="animate-ping" />}
        {/* Outer ring */}
        <circle
          r={size + 2}
          fill={isSelected ? color : 'transparent'}
          stroke={color}
          strokeWidth={2}
          opacity={isSelected ? 0.3 : 0.5}
        />
        {/* Inner circle */}
        <circle r={size} fill={color} stroke="#fff" strokeWidth={1.5} opacity={0.85} />
        {/* Count text */}
        <text
          textAnchor="middle"
          dominantBaseline="central"
          fill="#fff"
          fontSize={size > 12 ? 10 : 8}
          fontWeight="bold"
        >
          {port.totalContainers}
        </text>
      </g>
      {/* Tooltip on hover */}
      {hovered && (
        <g>
          <foreignObject
            x={size + 8}
            y={-70}
            width={220}
            height={140}
            className="pointer-events-none overflow-visible"
          >
            <div className="bg-popover border border-border rounded-lg shadow-lg p-3 text-xs">
              <p className="font-semibold text-sm mb-1">{port.port}</p>
              <p className="text-muted-foreground mb-2">{port.country}</p>
              <div className="space-y-1">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Contenedores:</span>
                  <span className="font-medium">{port.totalContainers}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Llenado prom.:</span>
                  <span className="font-medium" style={{ color }}>
                    {formatoPorcentaje(port.avgFillPercent, { decimales: 1 })}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Vol. disponible:</span>
                  <span className="font-medium">
                    {formatoNumero(port.totalAvailableVolumeCbm, 1)} m³
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Peso disponible:</span>
                  <span className="font-medium">
                    {formatoNumero(port.totalAvailableWeightKg, 0)} kg
                  </span>
                </div>
                {port.byStatus.empty > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Vacios:</span>
                    <span>{port.byStatus.empty}</span>
                  </div>
                )}
                {port.byStatus.loading > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Cargando:</span>
                    <span>{port.byStatus.loading}</span>
                  </div>
                )}
                {port.byStatus.sealed > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Sellados:</span>
                    <span>{port.byStatus.sealed}</span>
                  </div>
                )}
              </div>
            </div>
          </foreignObject>
        </g>
      )}
    </Marker>
  );
}

// ── Container Card Component ───────────────────────────────────

interface ContainerCardProps {
  container: AvailableContainer;
}

function ContainerCard({ container }: ContainerCardProps) {
  const navigate = useNavigate();
  const fillColor = getFillColorClass(container.fillPercent);
  const fillTextColor = getFillTextClass(container.fillPercent);

  return (
    <Card className="hover:shadow-md transition-shadow">
      <CardContent className="p-4 space-y-3">
        {/* Header: container number + type */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <BoxSelect className="w-4 h-4 text-primary" />
            <span className="font-mono font-semibold text-sm">
              {container.containerNumber || 'Sin número'}
            </span>
            <Badge variant="outline" className="text-xs">
              {container.containerType.code}
            </Badge>
          </div>
          <ContainerStatusBadge status={container.status} />
        </div>

        {/* Ports */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <MapPin className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="truncate">
            {container.originPort || '—'} <span className="mx-1">→</span>{' '}
            {container.destinationPort || '—'}
          </span>
        </div>

        {/* Order link */}
        <div className="flex items-center gap-2 text-sm">
          <Ship className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
          <button
            className="text-primary hover:underline font-medium text-left truncate"
            onClick={() => navigate(`/trade/embarques/${container.order.id}`)}
          >
            {container.order.orderNumber}
          </button>
          {container.itemCount > 0 && (
            <span className="text-xs text-muted-foreground">({container.itemCount} items)</span>
          )}
        </div>

        {/* Capacity bar */}
        <div>
          <div className="flex items-center justify-between text-xs mb-1">
            <span className="text-muted-foreground">Llenado</span>
            <span className={`font-semibold ${fillTextColor}`}>
              {formatoPorcentaje(container.fillPercent, { decimales: 1 })}
            </span>
          </div>
          <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${fillColor}`}
              style={{ width: `${Math.min(container.fillPercent, 100)}%` }}
            />
          </div>
        </div>

        {/* Available space */}
        <div className="grid grid-cols-2 gap-2 text-xs">
          <div className="flex items-center gap-1.5 p-2 bg-muted/50 rounded-md">
            <Ruler className="w-3 h-3 text-info" />
            <div>
              <p className="text-muted-foreground">Disponible</p>
              <p className="font-semibold">{formatoNumero(container.availableVolumeCbm, 2)} m³</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 p-2 bg-muted/50 rounded-md">
            <Weight className="w-3 h-3 text-alerta" />
            <div>
              <p className="text-muted-foreground">Disponible</p>
              <p className="font-semibold">{formatoNumero(container.availableWeightKg, 0)} kg</p>
            </div>
          </div>
        </div>

        {/* ETD */}
        {container.etd && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Calendar className="w-3 h-3" />
            <span>ETD: {formatDate(container.etd)}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Main Component ──────────────────────────────────────────────

export function ContainerAvailabilityView() {
  const { portSummaries, loadingAvailableContainers, loadPortSummaries } = useImportTrackingStore();

  const [selectedPort, setSelectedPort] = useState<string | null>(null);
  const [sortValue, setSortValue] = useState('fillPercent-asc');
  const containerGridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    console.log('[ContainerAvailability] Mounting, loading port summaries...');
    loadPortSummaries().then(() => {
      console.log(
        '[ContainerAvailability] Loaded, portSummaries:',
        useImportTrackingStore.getState().portSummaries
      );
    });
  }, []);

  // Parse sort value
  const [sortField, sortOrder] = useMemo(() => {
    const parts = sortValue.split('-');
    return [parts[0] as SortField, parts[1] as SortOrder];
  }, [sortValue]);

  // Get all containers (flattened from port summaries)
  const allContainers = useMemo(() => {
    return portSummaries.flatMap((p) => p.containers);
  }, [portSummaries]);

  // Filter by selected port
  const filteredContainers = useMemo(() => {
    if (!selectedPort) return allContainers;
    return allContainers.filter((c) => c.originPort === selectedPort);
  }, [allContainers, selectedPort]);

  // Sort containers
  const sortedContainers = useMemo(() => {
    const sorted = [...filteredContainers];
    sorted.sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'fillPercent':
          cmp = a.fillPercent - b.fillPercent;
          break;
        case 'availableVolumeCbm':
          cmp = a.availableVolumeCbm - b.availableVolumeCbm;
          break;
        case 'etd': {
          const dateA = a.etd ? new Date(a.etd).getTime() : Infinity;
          const dateB = b.etd ? new Date(b.etd).getTime() : Infinity;
          cmp = dateA - dateB;
          break;
        }
      }
      return sortOrder === 'desc' ? -cmp : cmp;
    });
    return sorted;
  }, [filteredContainers, sortField, sortOrder]);

  // Port filter options for dropdown
  const portFilterOptions = useMemo(() => {
    return [
      { value: '', label: 'Todos los puertos' },
      ...portSummaries.map((p) => ({
        value: p.port,
        label: `${p.port} (${p.totalContainers})`,
      })),
    ];
  }, [portSummaries]);

  // Handle port selection from map
  const handlePortSelect = (port: string) => {
    if (selectedPort === port) {
      setSelectedPort(null);
    } else {
      setSelectedPort(port);
      // Scroll to container grid
      setTimeout(() => {
        containerGridRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
    }
  };

  // Stats summary
  const totalContainers = portSummaries.reduce((s, p) => s + p.totalContainers, 0);
  const totalPorts = portSummaries.length;
  const totalAvailableVolume = portSummaries.reduce((s, p) => s + p.totalAvailableVolumeCbm, 0);
  const avgFill =
    totalContainers > 0
      ? portSummaries.reduce((s, p) => s + p.avgFillPercent * p.totalContainers, 0) /
        totalContainers
      : 0;

  if (loadingAvailableContainers) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-[400px] w-full rounded-lg" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-48 w-full rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Package className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold">{totalContainers}</p>
              <p className="text-xs text-muted-foreground">Contenedores disponibles</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-info-suave rounded-lg">
              <Globe className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-2xl font-bold">{totalPorts}</p>
              <p className="text-xs text-muted-foreground">Puertos activos</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-ok-suave rounded-lg">
              <Ruler className="w-5 h-5 text-ok" />
            </div>
            <div>
              <p className="text-2xl font-bold">{formatoNumero(totalAvailableVolume, 1)}</p>
              <p className="text-xs text-muted-foreground">m³ disponibles total</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div
              className={`p-2 rounded-lg ${avgFill < 40 ? 'bg-ok-suave ' : avgFill < 70 ? 'bg-alerta-suave ' : 'bg-alerta-suave '}`}
            >
              <BoxSelect className={`w-5 h-5 ${getFillTextClass(avgFill)}`} />
            </div>
            <div>
              <p className={`text-2xl font-bold ${getFillTextClass(avgFill)}`}>
                {formatoPorcentaje(avgFill, { decimales: 1 })}
              </p>
              <p className="text-xs text-muted-foreground">Llenado promedio</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* World Map Section */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Globe className="w-5 h-5 text-primary" />
              <CardTitle className="text-lg">Mapa de disponibilidad</CardTitle>
            </div>
            {selectedPort && (
              <Button variant="ghost" size="sm" onClick={() => setSelectedPort(null)}>
                <X className="w-4 h-4 mr-1" />
                Limpiar filtro: {selectedPort}
              </Button>
            )}
          </div>
          {/* Legend */}
          <div className="flex items-center gap-4 text-xs text-muted-foreground mt-2">
            <span className="font-medium">Nivel de llenado:</span>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-ok" />
              <span>&lt;40%</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-alerta" />
              <span>40-70%</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-alerta" />
              <span>70-90%</span>
            </div>
            <div className="flex items-center gap-1">
              <div className="w-3 h-3 rounded-full bg-bloqueo" />
              <span>&gt;90%</span>
            </div>
            <span className="ml-2 font-medium">Tamaño = cantidad de contenedores</span>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {portSummaries.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-muted-foreground">
              <Globe className="w-16 h-16 mb-4 opacity-30" />
              <p className="text-lg font-medium">Sin contenedores disponibles</p>
              <p className="text-sm">No hay contenedores que aún no hayan partido</p>
            </div>
          ) : (
            <div className="w-full overflow-hidden rounded-b-lg bg-muted ">
              <ComposableMap
                projection="geoMercator"
                projectionConfig={{
                  scale: 120,
                  center: [0, 20],
                }}
                width={800}
                height={400}
                style={{ width: '100%', height: 'auto' }}
              >
                <ZoomableGroup>
                  <Geographies geography={GEO_URL}>
                    {({ geographies }) =>
                      geographies.map((geo) => (
                        <Geography
                          key={geo.rpiKey || geo.properties?.name || Math.random()}
                          geography={geo}
                          fill="#e2e8f0"
                          stroke="#cbd5e1"
                          strokeWidth={0.5}
                          style={{
                            default: { outline: 'none' },
                            hover: { fill: '#cbd5e1', outline: 'none' },
                            pressed: { outline: 'none' },
                          }}
                        />
                      ))
                    }
                  </Geographies>
                  {portSummaries
                    .filter((port) => port.lat != null && port.lng != null)
                    .map((port) => (
                      <PortMarker
                        key={port.port}
                        port={port as PortSummary & { lat: number; lng: number }}
                        isSelected={selectedPort === port.port}
                        onSelect={handlePortSelect}
                      />
                    ))}
                </ZoomableGroup>
              </ComposableMap>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Container Cards Grid */}
      <div ref={containerGridRef}>
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Package className="w-5 h-5 text-primary" />
                  Contenedores disponibles
                  {selectedPort && (
                    <Badge variant="secondary" className="ml-2">
                      {selectedPort}
                    </Badge>
                  )}
                </CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  {sortedContainers.length} contenedor{sortedContainers.length !== 1 ? 'es' : ''}
                  {selectedPort ? ` en ${selectedPort}` : ' en total'}
                </p>
              </div>
              <div className="flex items-center gap-3">
                {/* Port filter */}
                <div className="w-52">
                  <SearchableSelect
                    options={portFilterOptions}
                    value={selectedPort || ''}
                    onValueChange={(v) => setSelectedPort(v || null)}
                    placeholder="Filtrar por puerto..."
                    searchPlaceholder="Buscar puerto..."
                  />
                </div>
                {/* Sort */}
                <div className="w-56">
                  <SearchableSelect
                    options={SORT_OPTIONS}
                    value={sortValue}
                    onValueChange={setSortValue}
                    placeholder="Ordenar por..."
                    searchPlaceholder="Buscar orden..."
                  />
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {sortedContainers.length === 0 ? (
              <div className="text-center py-12">
                <Package className="w-12 h-12 mx-auto text-muted-foreground mb-3 opacity-30" />
                <p className="text-muted-foreground">
                  {selectedPort
                    ? `No hay contenedores disponibles en ${selectedPort}`
                    : 'No hay contenedores disponibles'}
                </p>
                {selectedPort && (
                  <Button variant="outline" className="mt-4" onClick={() => setSelectedPort(null)}>
                    Ver todos los contenedores
                  </Button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {sortedContainers.map((container) => (
                  <ContainerCard key={container.id} container={container} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default ContainerAvailabilityView;
